import React, { useState, useEffect, useCallback } from 'react';
import { createClientComponentClient } from '@/integrations/supabase/client';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Search, Filter, BookOpen, GraduationCap, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LessonLog {
    id: string;
    user_id: string;
    unit_number: number;
    lesson_number: number;
    completed_at: string | null;
    metadata: {
        score?: number;
        passed?: boolean;
        weak_areas?: string[];
        strong_areas?: string[];
        class_name?: string;
    };
    users?: {
        email: string;
        name: string;
    };
}

const DEMO_STUDENT_ID = '00000000-0000-4000-a000-000000000a4a';

export const StudentEducationLogs: React.FC = () => {
    const [logs, setLogs] = useState<LessonLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const supabase = createClientComponentClient();

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('student_lesson_logs')
                .select(`
          *,
          users:user_id (email, name)
        `)
                .order('completed_at', { ascending: false })
                .limit(50);

            if (error) throw error;
            setLogs((data as any) || []);
        } catch (err) {
            console.error('Error fetching lesson logs:', err);
        } finally {
            setLoading(false);
        }
    }, [supabase]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const filteredLogs = logs.filter(log =>
        log.users?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.users?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.metadata?.class_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-8 space-y-8">
            <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                    <BookOpen className="w-7 h-7 text-emerald-500" />
                    Learning Activity &amp; Progress Logs
                </h2>
                <p className="text-slate-500 dark:text-slate-400 font-medium mt-1 text-sm">
                    Detailed real-time feedback, lesson completions, and performance tracking across your organization
                </p>
            </div>

            <div className="flex items-center gap-4 bg-white/50 dark:bg-white/5 p-4 rounded-2xl border border-white/60 dark:border-white/10">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                        placeholder="Search by learner, employee, or course..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white"
                    />
                </div>
                <Button onClick={fetchLogs} disabled={loading} variant="outline" className="flex items-center gap-2">
                    {loading ? 'Refreshing...' : 'Refresh Activity'}
                </Button>
            </div>

            {loading ? (
                <div className="space-y-4 animate-pulse">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-28 bg-slate-100 dark:bg-slate-800/50 rounded-2xl"></div>
                    ))}
                </div>
            ) : (
                <div className="space-y-4">
                    <AnimatePresence mode="popLayout">
                        {filteredLogs.map((log) => (
                            <motion.div
                                key={log.id}
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="group"
                            >
                                <Card className="p-6 bg-white/40 dark:bg-slate-900/60 backdrop-blur-sm border-white/60 dark:border-white/10 hover:shadow-xl hover:shadow-emerald-500/5 transition-all">
                                    <div className="flex flex-col md:flex-row justify-between gap-6">
                                        <div className="flex gap-4">
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl shadow-lg ${log.metadata?.passed ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400' : 'bg-amber-100 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400'}`}>
                                                {log.metadata?.passed ? <CheckCircle2 /> : <AlertCircle />}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-bold text-slate-900 dark:text-white">{log.users?.name || 'Unknown Learner'}</h4>
                                                    <span className="text-xs text-slate-400">•</span>
                                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{log.users?.email}</span>
                                                </div>
                                                <h3 className="text-lg font-black text-emerald-700 dark:text-emerald-400 mt-1">
                                                    {log.metadata?.class_name || 'General Subject'} • Unit {log.unit_number} Lesson {log.lesson_number}
                                                </h3>
                                                <div className="flex items-center gap-2 mt-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                    <Clock className="w-3 h-3" />
                                                    {log.completed_at ? new Date(log.completed_at).toLocaleString() : 'In Progress'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-row md:flex-col justify-between md:justify-center items-center md:items-end gap-2">
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mastery Score</p>
                                                <p className="text-3xl font-black text-slate-900 dark:text-white">{log.metadata?.score ?? '--'}%</p>
                                            </div>
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter border ${log.metadata?.passed ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800/40' : 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-100 dark:border-amber-800/40'}`}>
                                                {log.metadata?.passed ? 'Passed' : 'Needs Review'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 dark:border-white/10 pt-6">
                                        <div>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3 h-3 text-emerald-500" /> Areas of Strength
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {log.metadata?.strong_areas && log.metadata.strong_areas.length > 0 ? (
                                                    log.metadata.strong_areas.map((s, idx) => (
                                                        <span key={idx} className="bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-xs font-bold px-3 py-1 rounded-full border border-emerald-100 dark:border-emerald-800/40">
                                                            {s}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">No specific strengths recorded</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                                <AlertCircle className="w-3 h-3 text-amber-500" /> Areas for Growth
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {log.metadata?.weak_areas && log.metadata.weak_areas.length > 0 ? (
                                                    log.metadata.weak_areas.map((w, idx) => (
                                                        <span key={idx} className="bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-xs font-bold px-3 py-1 rounded-full border border-amber-100 dark:border-amber-800/40">
                                                            {w}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">No specific weaknesses recorded</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}

            {filteredLogs.length === 0 && !loading && (
                <div className="text-center py-16 bg-white/30 dark:bg-white/5 rounded-2xl border border-dashed border-slate-300 dark:border-white/10">
                    <BookOpen className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-700 dark:text-slate-200 font-bold text-base">No learning activity recorded yet</p>
                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">When learners start lessons, their completions, quiz scores, and AI grading will appear here live.</p>
                </div>
            )}
        </div>
    );
};
